import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { lojaApi } from '../../services/api';
import { LogoMark, Loading, Badge } from '../../components/ui';
import { formatarMoeda, formatarData } from '../../lib/format';
import { CheckCircle2, Clock, Package, MessageCircle } from 'lucide-react';

const statusMap: Record<string, { tom: string; label: string }> = {
  AGUARDANDO_PAGAMENTO: { tom: 'amber', label: 'Aguardando pagamento' },
  PAGO: { tom: 'green', label: 'Pagamento confirmado' },
  EM_PRODUCAO: { tom: 'blue', label: 'Em produção' },
  PRONTO: { tom: 'green', label: 'Pronto' },
  ENTREGUE: { tom: 'gray', label: 'Entregue' },
  CANCELADO: { tom: 'red', label: 'Cancelado' },
};

const pagamentoMap: Record<string, { tom: string; label: string }> = {
  PENDENTE: { tom: 'amber', label: 'Pagamento pendente' },
  APROVADO: { tom: 'green', label: 'Pagamento aprovado' },
  RECUSADO: { tom: 'red', label: 'Pagamento recusado' },
  ESTORNADO: { tom: 'red', label: 'Pagamento estornado' },
};

export function PedidoStatusPage() {
  const { id } = useParams();

  // Atualiza sozinho a cada 10s (útil na volta do checkout do Mercado Pago)
  const { data: pedido, isLoading } = useQuery({
    queryKey: ['loja-pedido', id],
    queryFn: () => lojaApi.statusPedido(id!).then(r => r.data),
    enabled: !!id,
    refetchInterval: 10000,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-ink-50 flex items-center justify-center">
        <Loading />
      </div>
    );
  }

  if (!pedido) {
    return (
      <div className="min-h-screen bg-ink-50 flex items-center justify-center p-6">
        <div className="card max-w-md w-full text-center">
          <p className="font-display text-lg font-bold text-ink-950">Pedido não encontrado</p>
          <Link to="/loja" className="btn-primary mt-5">Voltar para a loja</Link>
        </div>
      </div>
    );
  }

  const s = statusMap[pedido.status] || { tom: 'gray', label: pedido.status };
  const pg = pagamentoMap[pedido.pagamentoStatus] || { tom: 'gray', label: pedido.pagamentoStatus };
  const pago = pedido.pagamentoStatus === 'APROVADO' || pedido.status !== 'AGUARDANDO_PAGAMENTO';

  return (
    <div className="min-h-screen bg-ink-50">
      <header className="bg-white border-b border-ink-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-center">
          <Link to="/loja" className="flex items-center gap-2.5">
            <LogoMark size={30} />
            <span className="font-display font-bold text-ink-950 tracking-tight">MONCHERY</span>
          </Link>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 space-y-6">
        {/* Status principal */}
        <div className="card text-center py-10">
          <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-5 ${
            pago ? 'bg-emerald-50' : 'bg-amber-50'
          }`}>
            {pago ? (
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            ) : (
              <Clock className="w-8 h-8 text-amber-500" />
            )}
          </div>
          <h1 className="font-display text-2xl font-bold text-ink-950 tracking-tight">
            {pago ? 'Pedido confirmado!' : 'Pedido recebido!'}
          </h1>
          <p className="text-ink-500 mt-2">
            Obrigado, {pedido.clienteNome?.split(' ')[0]}. Seu pedido é o{' '}
            <span className="font-bold text-ink-900">#{pedido.numero}</span>
          </p>
          <div className="flex items-center justify-center gap-2 mt-5 flex-wrap">
            <Badge tom={s.tom} label={s.label} />
            <Badge tom={pg.tom} label={pg.label} />
            {pedido.pagamentoMetodo && (
              <Badge tom="brand" label={pedido.pagamentoMetodo === 'PIX' ? 'PIX' : 'Cartão'} />
            )}
          </div>

          {!pago && (
            <div className="mt-6 mx-auto max-w-sm p-4 rounded-xl bg-amber-50 ring-1 ring-inset ring-amber-600/15 text-[13px] text-amber-800">
              Assim que o pagamento for confirmado, seu pedido entra em produção
              automaticamente. Esta página atualiza sozinha.
            </div>
          )}
        </div>

        {/* Itens */}
        <div className="card">
          <h2 className="card-title flex items-center gap-2 mb-4">
            <Package className="w-4 h-4 text-brand-600" />
            Itens do pedido
          </h2>
          <div className="divide-y divide-ink-100">
            {pedido.itens?.map((i: any, idx: number) => (
              <div key={idx} className="py-3 flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-ink-800">
                    {i.quantidade}× {i.produtoNome}
                  </p>
                  <p className="text-xs text-ink-400 mt-0.5">
                    {i.cor} · {i.tamanho}
                    {i.estampaNome ? ` · ${i.estampaNome}` : ' · Lisa'}
                  </p>
                </div>
                <p className="text-sm font-bold text-ink-900">{formatarMoeda(i.valorTotal)}</p>
              </div>
            ))}
          </div>
          <div className="flex justify-between pt-4 mt-2 border-t border-ink-100">
            <span className="font-semibold text-ink-900">Total</span>
            <span className="font-display text-lg font-bold text-ink-950">
              {formatarMoeda(pedido.valorTotal)}
            </span>
          </div>
          <p className="text-xs text-ink-400 mt-3">Pedido feito em {formatarData(pedido.criadoEm)}</p>
        </div>

        {/* Ações */}
        <div className="flex flex-col sm:flex-row gap-3">
          <a
            href={`https://wa.me/?text=${encodeURIComponent(`Olá! Fiz o pedido #${pedido.numero} na loja Monchery e gostaria de acompanhar.`)}`}
            target="_blank"
            rel="noreferrer"
            className="btn-secondary flex-1"
          >
            <MessageCircle className="w-4 h-4" />
            Falar com a loja
          </a>
          <Link to="/loja" className="btn-primary flex-1">
            Voltar para a loja
          </Link>
        </div>
      </div>
    </div>
  );
}
